import React, { useEffect, useState } from 'react';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import CurrencyText from '../../../../../components/ui/labels/CurrencyText';
import { Pen, ShieldCheck, SquarePen } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatters';
import CustomActionButton from '../../../../../components/ui/buttons/CustomActionButton';
import ProtectionsModal from '../../../../../components/modals/protections/ProtectionsModal';
import DonutChart2 from '../../../../../components/ui/charts/DonutChart2';
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf';
import RedefineProtectionsModal from '../../../../../components/modals/protections/RedefineProtectionsModal';
import Button from '../../../../../components/ui/buttons/Button';
import LifeExpectancyModal from '../../../../../components/modals/protections/LifeExpectancyModal';
import BlurScopeContent from '../../../../../components/ui/overlays/BlurScopeContent';

const ProtectionsTab = ({ clientId, onSuccess, scope, canWrite }) => {
  const { addToast } = useToast();

  const [data, setData] = useState({
    deathProtections: [],
    livingProtections: [],
    deathProtectionsTotal: 0,
    livingProtectionsTotal: 0,
    clientData: {
      id: null,
      birthDate: null,
      age: null,
      lifeExpectancy: null,
    },
    percentInCoverages: 0,
    coveragesTotal: 0,
    chartData: [],
    coveragesTotal: 0,
    spouseId: null,
  });

  const [showEditProtectionsModal, setShowEditProtectionsModal] = useState(false);
  const [transactionType, setTransactionType] = useState('death'); // 'death' | 'living'
  const [isResetting, setIsResetting] = useState(false);
  const [showRedefineProtectionsModal, setShowRedefineProtectionsModal] = useState(false);
  const [showEditLifeExpectancyModal, setShowEditLifeExpectancyModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const handleModalSuccess = () => {
    setShowEditProtectionsModal(false);
    setShowEditLifeExpectancyModal(false);
    fetchData();
  };

  const fetchData = () => {
    fetch(`/app/planner/client/${clientId}/dashboard/protections?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);

        setData(camelData);
      });
  };

  const handleRedefineRecommendations = async () => {
    if (isResetting) return;
    setIsResetting(true);

    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/protections/redefine?scope=${scope}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (res.status === 204) {
        fetchData();
        addToast({ type: 'success', message: 'Recomendação redefinida.' });
        setIsResetting(false);
        return;
      }

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Falha ao redefinir as coberturas');
      }

      addToast({ type: 'success', message: 'Recomendação redefinida.' });
      fetchData();
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', message: 'Não foi possível redefinir as coberturas. Tente novamente.' });
    } finally {
      setIsResetting(false);
    }
  };

  const tabBody = (
    <div className="grid grid-cols-6 gap-5">
      {/* coluna 1 */}
      <div className="col-span-2 flex flex-col gap-5">
        <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
          <h2 className="text-xl text-blueMedium font-semibold text-center">Distribuição das coberturas por Morte</h2>
          {data.chartData.length > 0 ? (
            <DonutChart2 data={data.chartData} height={300} />
          ) : (
            <p className="text-gray-500 text-center">Nenhum dado disponível</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-5">
          <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
            <h3 className="text-lg text-blueDarker font-semibold">Nascimento</h3>
            <span className="text-tealMedium text-xl font-semibold">{data.clientData.birthDate}</span>
          </div>
          <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
            <h3 className="text-lg text-blueDarker font-semibold">Idade</h3>
            <span className="text-tealMedium text-xl font-semibold">{data.clientData.age} anos</span>
          </div>
          <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
            <h3 className="text-lg text-blueDarker font-semibold">Exp. de vida (morte)</h3>
            <div className="flex items-center gap-3">
              <span className="text-tealMedium text-xl font-semibold">{data.clientData.lifeExpectancy} anos</span>
              {canWrite && (
                <SquarePen
                  className="text-tealMedium"
                  onClick={() => setShowEditLifeExpectancyModal(true)}
                  size={18}
                  strokeWidth={1.5}
                  style={{ cursor: 'pointer' }}
                />
              )}
            </div>
          </div>
          <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-2">
            <h3 className="text-lg text-blueDarker font-semibold">Exp. de vida (invalidez)</h3>
            <div className="flex items-center gap-3">
              <span className="text-tealMedium text-xl font-semibold">
                {data.clientData.invalidityLifeExpectancy} anos
              </span>
              {canWrite && (
                <SquarePen
                  className="text-tealMedium"
                  onClick={() => setShowEditLifeExpectancyModal(true)}
                  size={18}
                  strokeWidth={1.5}
                  style={{ cursor: 'pointer' }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="rounded-custom20 px-6 flex flex-col gap-2">
          <div className="flex justify-center items-center gap-3">
            {canWrite && (
              <Button state="alt" onClick={() => setShowRedefineProtectionsModal(true)}>
                REDEFINIR RECOMENDAÇÃO
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* coluna 2 */}
      <div className="flex flex-col gap-5 col-span-4">
        <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-4">
          <div className="flex justify-between items-center gap-3 mt-2">
            <h2 className="text-xl text-blueMedium font-semibold">Coberturas por Morte</h2>
            {canWrite && (
              <CustomActionButton
                state="alt"
                onClick={() => {
                  setTransactionType('death');
                  setShowEditProtectionsModal(true);
                }}
              >
                EDITAR
              </CustomActionButton>
            )}
          </div>

          {data.deathProtections.length > 0 ? (
            <div className="overflow-y-auto flex flex-col gap-3 max-h-custom260">
              {data.deathProtections.map((protection, index) => (
                <div
                  key={`death-coverage-${index}`}
                  className="rounded-xl px-5 py-3 grid grid-cols-3 items-center bg-blueLight"
                >
                  <div className="flex gap-4 items-center">
                    <span className="rounded-full bg-tealMedium flex justify-center items-center w-8 h-8">
                      <ShieldCheck className="text-white" size={20} strokeWidth={2.25} />
                    </span>
                    <span className="text-blueHighlight text-lg font-semibold">{protection.name}</span>
                  </div>
                  <div className="flex justify-center">
                    <span className="text-blueDarker text-lg">
                      {parseInt(protection.percentOfTotal)}% da Cobertura total
                    </span>
                  </div>
                  <div className="flex justify-end">
                    <span className="text-blueDarker text-lg font-medium">{formatCurrency(protection.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-blueSteelDark">Nenhuma cobertura por morte cadastrada.</span>
          )}

          <div className="flex justify-end items-center gap-3 mt-4">
            <span className="text-sm text-tealBase font-medium">TOTAL: </span>
            <span className="text-xl text-blueDarker font-medium">{formatCurrency(data.deathProtectionsTotal)}</span>
          </div>
        </div>

        <div className="bg-white rounded-custom20 px-6 py-4 flex flex-col gap-4">
          <div className="flex justify-between items-center gap-3 mt-2">
            <h2 className="text-xl text-blueMedium font-semibold">Coberturas em Vida</h2>
            {canWrite && (
              <CustomActionButton
                state="alt"
                onClick={() => {
                  setTransactionType('death');
                  setShowEditProtectionsModal(true);
                }}
              >
                EDITAR
              </CustomActionButton>
            )}
          </div>

          {data.livingProtections.length > 0 ? (
            <div className="overflow-y-auto flex flex-col gap-3 max-h-custom260">
              {data.livingProtections.map((protection, index) => (
                <div
                  key={`living-coverage-${index}`}
                  className="rounded-xl px-5 py-3 grid grid-cols-2 items-center bg-blueLight"
                >
                  <div className="flex gap-4 items-center">
                    <span className="rounded-full bg-tealMedium flex justify-center items-center w-8 h-8">
                      <ShieldCheck className="text-white" size={20} strokeWidth={2.25} />
                    </span>
                    <span className="text-blueHighlight text-lg font-semibold">{protection.name}</span>
                  </div>
                  <div className="flex justify-end">
                    <span className="text-blueDarker text-lg font-medium">{formatCurrency(protection.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-blueSteelDark">Nenhuma cobertura em vida cadastrada.</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      {scope === 'couple' ? (
        <BlurScopeContent
          title='Indisponível para a visualização "casal"'
          subtitle="Altere para Cliente ou Cônjuge para ver e editar as proteções."
        >
          <div className="pointer-events-none select-none">{tabBody}</div>
        </BlurScopeContent>
      ) : (
        tabBody
      )}

      {/* Monta os modais apenas quando NÃO estiver borrado */}
      {scope !== 'couple' && showEditProtectionsModal && (
        <ProtectionsModal
          clientId={scope === 'client' ? clientId : data.spouseId}
          scope={scope}
          onClose={() => setShowEditProtectionsModal(false)}
          onSuccess={handleModalSuccess}
          transactionType={transactionType}
        />
      )}

      {scope !== 'couple' && showRedefineProtectionsModal && (
        <RedefineProtectionsModal
          clientId={scope === 'client' ? clientId : data.spouseId}
          scope={scope}
          onClose={() => setShowRedefineProtectionsModal(false)}
          onSuccess={() => {
            fetchData();
            onSuccess && onSuccess();
          }}
        />
      )}

      {scope !== 'couple' && showEditLifeExpectancyModal && (
        <LifeExpectancyModal
          clientId={scope === 'client' ? clientId : data.spouseId}
          scope={scope}
          onClose={() => setShowEditLifeExpectancyModal(false)}
          onSuccess={handleModalSuccess}
          lifeExpectancy={data.clientData.lifeExpectancy}
          invalidityLifeExpectancy={data.clientData.invalidityLifeExpectancy}
        />
      )}
    </div>
  );
};

export default ProtectionsTab;
